import { useDisclosure } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useClients } from 'src/clients/hooks/useClients';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { AudioPlayer } from 'src/meetings/detail/tabs/transcript/AudioPlayer';
import { ContentPaneOverlay } from 'src/UI/app-container/ContentPaneOverlay';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import PageLoader from 'src/UI/loader/PageLoader';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { useDocumentAudioPlayer } from 'src/data-capture/hooks/useDocumentAudioPlayer';
import { isAudioFile } from 'src/data-capture/file-management/allowedFileTypes';
import FileUploadLayout from 'src/data-capture/file-management/FileUploadLayout';
import FiledDocumentInlineActions from 'src/data-capture/file-management/components/FiledDocumentInlineActions';
import RenameDocumentControl from 'src/data-capture/file-management/components/RenameDocumentControl';
import FiledDocumentsHeaderActions from 'src/data-capture/file-management/components/FiledDocumentsHeaderActions';
import SelectionHeaderActions from 'src/data-capture/file-management/components/SelectionHeaderActions';
import { useFiledDocumentsView } from 'src/data-capture/file-management/hooks/useFiledDocumentsView';
import { useBulkDocumentSelection } from 'src/data-capture/file-management/hooks/useBulkDocumentSelection';
import BulkDeleteConfirmModal from 'src/data-capture/file-management/components/BulkDeleteConfirmModal';
import { downloadViaAnchor } from 'src/data-capture/file-management/utils/downloadViaAnchor';
import FilesTable from 'src/data-capture/file-management/files-table/FilesTable';
import { filedDocumentColumns } from 'src/data-capture/file-management/files-table/filedDocumentColumns';
import { buildFiledDocumentRowActions } from 'src/data-capture/file-management/files-table/filedDocumentRowActions';
import { RenameFolderModal } from 'src/data-capture/file-management/modals/RenameFolderModal';
import { CreateFolderModal } from 'src/data-capture/file-management/modals/CreateFolderModal';
import MoveFileModal from 'src/data-capture/file-management/files-table/MoveFileModal';
import {
  buildFamilyRootRecords,
  CategoryRow,
  filterFilesByScope,
  FamilyMember,
} from 'src/data-capture/file-management/crossEntityFolders';
import {
  buildFileTree,
  filterTreeIds,
  flattenVisible,
  initialExpandedIds,
} from 'src/data-capture/file-management/fileTree';
import type { FlatRow } from 'src/data-capture/file-management/fileTree';
import { useFamilyFileActions } from './useFamilyFileActions';
import { useFamilyFileUploadActions } from './useFamilyFileUploadActions';

export default function FamilyFilesPage() {
  const {
    familyId = '',
    sectionSlug,
    folderId,
  } = useParams<{
    familyId: string;
    sectionSlug?: string;
    folderId?: string;
  }>();

  const navigate = useNavigate();
  const { family, isLoading: isFamilyLoading } = useFamily(familyId);
  const familyName = family?.name;

  const { clients, isLoading: isClientsLoading } = useClients(
    { familyId },
    { enabled: !!familyId },
  );
  const clientNameById = Object.fromEntries(
    clients.map(c => [c.id, `${c.firstName} ${c.lastName}`]),
  );

  const orderedMembers: FamilyMember[] = (family?.members ?? []).map(m => ({
    clientId: m.clientId,
    displayName: clientNameById[m.clientId] ?? 'Family member',
  }));

  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [selectedFolderForRename, setSelectedFolderForRename] = useState<UploadedFile | null>(null);
  const [renameModalOpened, { open: openRenameModal, close: closeRenameModal }] =
    useDisclosure(false);
  const [fileToDelete, setFileToDelete] = useState<UploadedFile | null>(null);
  const [createFolderOpened, { open: openCreateFolder, close: closeCreateFolder }] =
    useDisclosure(false);
  const [createFolderParent, setCreateFolderParent] = useState<UploadedFile | null>(null);

  const { files, allOtherFiles, isLoading, error, refetchFiles } = useFileUploads({
    familyId,
    autoRefreshProcessing: true,
  });

  const { deleteFile, deleteError, resetDeleteError, isDeleting, moveFile, renameFolder } =
    useFamilyFileActions({ familyId });

  const { createFolder } = useFamilyFileUploadActions({ familyId });

  useEffect(() => {
    refetchFiles();
  }, [sectionSlug, folderId]);

  const familyOwnedAllOtherFiles = filterFilesByScope(allOtherFiles, { kind: 'family-shared' });

  const { searchValue, setSearchValue, searchOpen, setSearchOpen, filteredFiles } =
    useFiledDocumentsView({
      files,
      allOtherFiles: familyOwnedAllOtherFiles,
      currentFolder: null,
      routeKey: `${sectionSlug ?? ''}`,
    });

  const box1Tree = buildFileTree(familyOwnedAllOtherFiles);

  const allOtherFileIds = new Set(allOtherFiles.map(f => f.id));

  const memberCategoryEntries = orderedMembers.flatMap(member => {
    const memberFiles = filterFilesByScope(files, {
      kind: 'member',
      clientId: member.clientId,
    });
    const scopedMemberFiles = memberFiles.filter(f => allOtherFileIds.has(f.id));
    const row = buildFamilyRootRecords(files, [member]).find(
      (r): r is CategoryRow => 'categoryScope' in r,
    );
    if (!row) return [];
    return [{ row, files: scopedMemberFiles }];
  });

  const box2Tree = buildFileTree([], memberCategoryEntries);

  const [box1ExpandedIds, setBox1ExpandedIds] = useState<Set<string>>(new Set<string>());
  const box1SeededRef = useRef(false);
  useEffect(() => {
    if (box1SeededRef.current) return;
    if (box1Tree.length === 0) return;
    box1SeededRef.current = true;
    setBox1ExpandedIds(initialExpandedIds(box1Tree));
  }, [box1Tree]);

  // Guard on family+clients loading so the seed fires only once all data sources have resolved.
  const [box2ExpandedIds, setBox2ExpandedIds] = useState<Set<string>>(new Set<string>());
  const box2SeededRef = useRef(false);
  useEffect(() => {
    if (isLoading) return;
    if (isFamilyLoading || isClientsLoading) return;
    if (box2SeededRef.current) return;
    if (box2Tree.length === 0) return;
    box2SeededRef.current = true;

    const ids = new Set<string>();
    for (const node of box2Tree) {
      if (node.record.isFolder) ids.add(node.record.id);
      for (const child of node.children) {
        if (child.record.isFolder) ids.add(child.record.id);
      }
    }
    setBox2ExpandedIds(ids);
  }, [isLoading, isFamilyLoading, isClientsLoading, box2Tree]);

  const handleBox1ToggleExpand = (id: string) => {
    setBox1ExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleBox2ToggleExpand = (id: string) => {
    setBox2ExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const hasMembers = orderedMembers.length > 0;
  const searchTerm = searchValue.trim().toLowerCase();

  const box1SearchIds = searchTerm ? filterTreeIds(box1Tree, searchTerm) : null;

  let box1VisibleRows: FlatRow[] = [];
  if (box1SearchIds) {
    box1VisibleRows = flattenVisible(box1Tree, box1SearchIds).filter(r =>
      box1SearchIds.has(r.record.id),
    );
  } else {
    box1VisibleRows = flattenVisible(box1Tree, box1ExpandedIds);
  }

  const box2SearchIds = hasMembers && searchTerm ? filterTreeIds(box2Tree, searchTerm) : null;

  let box2VisibleRows: FlatRow[] = [];
  if (box2SearchIds) {
    box2VisibleRows = flattenVisible(box2Tree, box2SearchIds).filter(r =>
      box2SearchIds.has(r.record.id),
    );
  } else {
    box2VisibleRows = flattenVisible(box2Tree, box2ExpandedIds);
  }

  const treeSearchResultCount = (() => {
    if (!searchTerm) return filteredFiles.length;
    const box1Count = box1VisibleRows.filter(r => !r.record.isFolder).length;
    const box2Count = box2VisibleRows.filter(r => !r.record.isFolder).length;
    return box1Count + box2Count;
  })();

  const { createLink } = useFileTemporaryLinkMutation();

  const {
    selection,
    selectedRecords,
    bulkDeleteNames,
    requestBulkDelete,
    cancelBulkDelete,
    confirmBulkDelete,
    isBulkDeleting,
    isBulkDownloading,
    downloadSelected,
  } = useBulkDocumentSelection({
    records: familyOwnedAllOtherFiles,
    entityName: 'file',
    nameOf: record => record.file.fileName,
    deleteRecord: record => deleteFile(record),
    downloadRecord: record => handleDownloadFile(record),
    refetch: refetchFiles,
    logContext: { familyId },
  });

  const audioPlayer = useDocumentAudioPlayer();

  const handleDeletePress = (file: UploadedFile) => {
    resetDeleteError();
    setFileToDelete(file);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await deleteFile(fileToDelete);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        e,
        { familyId, clientId: fileToDelete.clientId, fileId: fileToDelete.id },
      );
    }
    setFileToDelete(null);
  };

  const handleDownloadFile = async (uploadedFile: UploadedFile) => {
    try {
      await downloadViaAnchor(createLink, uploadedFile.file.id, uploadedFile.file.fileName);
    } catch (error) {
      notifyAndLogError(
        'Failed to download file',
        'An error occurred while trying to download the file. Please try again later.',
        error,
        { familyId, fileId: uploadedFile.file.id },
      );
    }
  };

  const handleDownloadPress = (file: UploadedFile) => {
    if (isAudioFile(file.file.fileType, file.file.fileName)) {
      audioPlayer.download(file, file.clientId);
      return;
    }
    handleDownloadFile(file);
  };

  const handleFileDocument = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { familyId, clientId: file.clientId, fileId: file.id, parentId },
      );
    }
  };

  const handleOpenCreateFolder = () => {
    setCreateFolderParent(null);
    openCreateFolder();
  };

  const handleCreateSubfolderClick = (folder: UploadedFile) => {
    setCreateFolderParent(folder);
    openCreateFolder();
  };

  const handleCloseCreateFolder = () => {
    closeCreateFolder();
    setCreateFolderParent(null);
  };

  const handleCreateFolder = async (folderName: string) => {
    const parent = createFolderParent;
    try {
      await createFolder(folderName, parent?.id);
      if (parent) {
        setBox1ExpandedIds(prev => new Set(prev).add(parent.id));
      }
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to create folder',
        'An error occurred while trying to create the folder. Please try again later.',
        error,
        { familyId, folderName, parentId: parent?.id },
      );
      throw error;
    }
  };

  const handleRenameFolderClick = (folder: UploadedFile) => {
    setSelectedFolderForRename(folder);
    openRenameModal();
  };

  const handleRenameFolder = async (folder: UploadedFile, newName: string) => {
    await renameFolder(folder, newName);
    await refetchFiles();
  };

  const renderFileRenameSuffix = (file: UploadedFile) =>
    !file.isFolder && !('categoryScope' in file) ? (
      <RenameDocumentControl
        fileId={file.file.id}
        currentName={file.file.fileName}
        variant="plain"
      />
    ) : null;

  if (error) {
    return <GenericErrorPage error={error} />;
  }

  // isCreatingLink is intentionally excluded — including it would blank the page on every download.
  const isPageLoading = isLoading || isClientsLoading || isFamilyLoading;

  if (isPageLoading) {
    return <PageLoader absolute={false} />;
  }

  const box2Section = (
    <FilesTable<UploadedFile>
      entitiesWithFile={box2VisibleRows.map(r => r.record) as UploadedFile[]}
      isLoading={false}
      additionalColumns={filedDocumentColumns}
      treeRows={box2VisibleRows}
      onToggleExpand={handleBox2ToggleExpand}
      inlineActions={file => (
        <FiledDocumentInlineActions file={file} onDownload={handleDownloadPress} />
      )}
      onClick={() => {}}
      readOnly
      variant="compact"
      nameColumnTitle="Document name"
    />
  );

  return (
    <FileUploadLayout
      title={selection.isSelecting ? 'Select the files' : 'Filed documents'}
      familyId={familyId}
      familyName={familyName}
      variant="compact"
      collapsibleSearch={!selection.isSelecting}
      selectedIds={selection.isSelecting ? selection.selectedIds : undefined}
      searchOpen={searchOpen}
      onSearchOpenChange={setSearchOpen}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchResultCount={treeSearchResultCount}
      searchPlaceholder="Search documents"
      actions={
        selection.isSelecting ? (
          <SelectionHeaderActions
            selectedCount={selectedRecords.length}
            onDeleteSelected={requestBulkDelete}
            onDownloadSelected={downloadSelected}
            onCancel={selection.exit}
            isDownloading={isBulkDownloading}
          />
        ) : (
          <FiledDocumentsHeaderActions
            onCreateFolder={handleOpenCreateFolder}
            onUpload={() => navigate(`/family/${familyId}/documents/add`)}
          />
        )
      }
      secondarySection={hasMembers ? box2Section : undefined}
      secondaryTitle={hasMembers ? 'Individuals files' : undefined}
    >
      <FilesTable<UploadedFile>
        entitiesWithFile={box1VisibleRows.map(r => r.record) as UploadedFile[]}
        isLoading={isLoading}
        additionalColumns={filedDocumentColumns}
        treeRows={box1VisibleRows}
        onToggleExpand={handleBox1ToggleExpand}
        nameSuffix={renderFileRenameSuffix}
        inlineActions={file => (
          <FiledDocumentInlineActions file={file} onDownload={handleDownloadPress} />
        )}
        onClick={file => {
          if (!file.isFolder) return;
        }}
        enableDragDrop
        actions={file =>
          buildFiledDocumentRowActions(file, {
            onPlay: f => audioPlayer.play(f.file.id, f.file.fileName),
            onCreateSubfolder: handleCreateSubfolderClick,
            onRenameFolder: handleRenameFolderClick,
            onMove: handleFileDocument,
            onDelete: handleDeletePress,
            onSelect: f => selection.enter(f.id),
          })
        }
        variant="compact"
        nameColumnTitle="Document name"
        selection={
          selection.isSelecting
            ? {
                selectedIds: selection.selectedIds,
                onToggle: selection.toggle,
                isSelectable: record => !record.isFolder,
              }
            : undefined
        }
      />

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          folders={files.filter(f => !f.clientId)}
          rootLabel="Filed documents"
          onFileMoved={handleMoveFile}
        />
      )}

      <CreateFolderModal
        opened={createFolderOpened}
        onClose={handleCloseCreateFolder}
        onCreateFolder={handleCreateFolder}
        parentFolderName={createFolderParent?.file.fileName}
      />

      {selectedFolderForRename && (
        <RenameFolderModal
          opened={renameModalOpened}
          onClose={closeRenameModal}
          folder={selectedFolderForRename}
          onRename={handleRenameFolder}
        />
      )}

      <ConfirmDeleteModal
        opened={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={fileToDelete?.isFolder ? 'Delete folder' : 'Delete file'}
        message={`Are you sure you want to delete "${fileToDelete?.file.fileName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />

      <BulkDeleteConfirmModal
        fileNames={bulkDeleteNames}
        entityName="file"
        onClose={cancelBulkDelete}
        onConfirm={confirmBulkDelete}
        isLoading={isBulkDeleting}
      />

      {audioPlayer.isVisible && (
        <ContentPaneOverlay>
          <AudioPlayer
            isPlaying={audioPlayer.isPlaying}
            currentTime={audioPlayer.currentTime}
            duration={audioPlayer.duration}
            isMuted={audioPlayer.isMuted}
            onPlayPause={audioPlayer.handlePlayPause}
            onSeek={audioPlayer.handleSeek}
            onMuteToggle={audioPlayer.handleMuteToggle}
            fileName={audioPlayer.fileName}
            onClose={audioPlayer.handleClose}
          />
        </ContentPaneOverlay>
      )}
    </FileUploadLayout>
  );
}
